import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';

import GapSequenceWrapper from 'analyses/alignment/GapSequenceWrapper';
import {assert} from 'Asserts';
import {Part} from 'biojs/Part';
import {ComponentsSequence} from 'components/Sequence';
import SequenceSearchView, {
  SequenceSearchResult,
  SequenceSearchViewInitializationOptions,
} from 'components/SequenceSearchView';
import InsertPartSequenceTemplate, {InsertPartSequenceProps} from 'molbio/parts/InsertPartSequenceTemplate';
import InsertPartTemplate, {InsertPartProps} from 'molbio/parts/InsertPartTemplate';
import notifications from 'notifications';
import * as popup from 'Popup';

export interface InsertPartModalViewOptions extends Backbone.ViewOptions<Backbone.Model> {
  sequence: ComponentsSequence | GapSequenceWrapper;
  oldPart?: Part;
}

class InsertPartModalView extends popup.Popup<Backbone.Model> {
  oldPart?: Part;
  searchView!: SequenceSearchView;
  sequence!: ComponentsSequence | GapSequenceWrapper;
  title!: string;

  template!: (props: InsertPartProps) => JSX.Element;
  sequenceTemplate!: (props: InsertPartSequenceProps) => string;

  static override initClass(): void {
    this.prototype.template = InsertPartTemplate;
    this.prototype.sequenceTemplate = InsertPartSequenceTemplate;
    this.prototype.title = 'Choose Part';
  }

  override form(): void {
    this.$body![0].innerHTML = ReactDOMServer.renderToStaticMarkup(
      <InsertPartTemplate nucleotideType={this.sequence.nucleotideType()} />
    );
    this.choice({
      type: 'submit',
      text: 'Choose Part',
    });
    this.choice({
      type: 'cancel',
      text: 'Cancel',
    });

    this.searchView.setElement(this.$body!.find('.search'));
    this.searchView.render();
  }

  _getPath(sequence: SequenceSearchResult): string {
    const regex = /\/([^/]+)\//; // "/MATCHES_BETWEEN_FIRST_SLASHES/blah/blah"
    const match = regex.exec(sequence.get('editURL'));
    const folder = sequence.folder().name;
    if (match != null) {
      return `${match[1]}/${folder}`;
    } else {
      return folder;
    }
  }

  override initialize({sequence, oldPart}: InsertPartModalViewOptions): void {
    this.sequence = sequence;
    this.oldPart = oldPart;
    this.selectSequence = this.selectSequence.bind(this);
    const searchViewOptions: SequenceSearchViewInitializationOptions = {
      // eslint-disable-next-line @typescript-eslint/unbound-method -- this is bound directly above
      selectSequence: this.selectSequence,
      nucleotideType: this.sequence.nucleotideType(),
      excludeModified: true,
    };
    this.searchView = new SequenceSearchView(searchViewOptions);
  }

  selectSequence(sequence: SequenceSearchResult): void {
    this.$('.selected-sequence')
      .empty()
      .data('sequence', sequence)
      // eslint-disable-next-line @typescript-eslint/unbound-method
      .append(this.sequenceTemplate({sequence, getPath: this._getPath}));

    if (sequence.get('circular')) {
      this.$('.warning').html(
        '<b>Note:</b> The selected sequence is <b>circular</b>, but will be inserted as a <b>linear</b> sequence.'
      );
    } else {
      this.$('.warning').text('');
    }
    this.check();
  }

  override focus(): void {
    this.searchView.focus();
  }

  // @ts-expect-error Incorrectly extends parent
  override validate(): [SequenceSearchResult, boolean] | false {
    const sequence = $('.selected-sequence').data('sequence');
    const reverseComplemented = this.$('.reverse-complement').is(':checked');
    if (sequence != null) {
      return [sequence, reverseComplemented];
    } else {
      return false;
    }
  }

  override submit(): JQuery {
    // We _should_ only get here if `validate` passes and returns the tuple; parent will not call through to
    // this otherwise
    const validateResult = this.validate();
    assert(validateResult !== false, 'Part insert failed validation!');
    const [sequence, reverseComplemented] = validateResult;
    let options = {reverse: false, complement: false};
    if (reverseComplemented) {
      options = {reverse: true, complement: true};
    }

    let start;
    const selection = this.sequence.selection();
    if (this.oldPart != null || !selection.isEmpty()) {
      start = this.oldPart ? this.oldPart.start() : selection.start();
      const end = this.oldPart ? this.oldPart.end() : selection.end();
      this.sequence.replaceWithPart(start, end!, sequence.id, options).fail(notifications.notifyError);
    } else {
      this.sequence.insertPart(selection.start(), sequence, options).fail(notifications.notifyError);
    }
    return this.close();
  }
}

InsertPartModalView.initClass();

export default InsertPartModalView;
